/**
 * Um bloco de trilha dos Primeiros Passos: título, "N de M", o que a trilha libera, o aviso
 * de bloqueio (que nomeia a trilha que falta) e as linhas de item (spec #387, seção 5.3).
 *
 * Trilha opcional diz "não trava nada" e nunca leva aviso de bloqueio. O aviso de bloqueio é
 * informação, não alarme: usa o tom neutro do tema, porque só o botão do próximo passo pode
 * disputar o olhar.
 */

import React from 'react';
import { Box, Chip, Link, Typography, useTheme } from '@mui/material';
import {
  Business as ClientIcon,
  EventAvailable as DailyIcon,
  Group as AccountIcon,
  HealthAndSafety as RiskIcon,
  InfoOutlined as InfoIcon,
  LocalHospital as OperationIcon,
  Lock as LockIcon,
  Notifications as ChannelsIcon,
  Settings as InstallIcon,
} from '@mui/icons-material';

import { SectionCard } from '../../../components';
import type { Perspectiva } from '../../../types/manifesto';
import type { OnboardingTrack, TrackKey } from '../../../types/onboarding';
import type { ManifestoDeAcesso } from '../proximoPasso';
import { ItemRow } from './ItemRow';
import {
  DESCRICAO_DA_OPERACAO_DIARIA,
  escolher,
  FRASES,
  TRILHAS,
  type TrilhaTexto,
} from '../textos';

const ICONE_DA_TRILHA: Record<TrackKey, React.ReactNode> = {
  install: <InstallIcon />,
  account: <AccountIcon />,
  operation: <OperationIcon />,
  client: <ClientIcon />,
  risk: <RiskIcon />,
  daily: <DailyIcon />,
  channels: <ChannelsIcon />,
};

/** Só o que entra em "N de M" — informativo e opcional não são passo de ninguém. */
function contagem(trilha: OnboardingTrack): { feitos: number; total: number } {
  const contados = trilha.items.filter((i) => ['done', 'warn', 'pending', 'locked'].includes(i.state));
  return {
    feitos: contados.filter((i) => i.state === 'done' || i.state === 'warn').length,
    total: contados.length,
  };
}

function descricaoDaTrilha(
  trilha: OnboardingTrack,
  texto: TrilhaTexto,
  perspectiva: Perspectiva
): string[] {
  const frases: string[] = [];
  if (texto.libera) frases.push(`Libera: ${texto.libera}.`);

  if (trilha.key === 'daily') {
    frases.push(
      trilha.blocked ? DESCRICAO_DA_OPERACAO_DIARIA.bloqueada : DESCRICAO_DA_OPERACAO_DIARIA.liberada
    );
  } else if (texto.descricao) {
    frases.push(escolher(texto.descricao, perspectiva));
  }

  if (trilha.moduleScope) {
    frases.push(
      trilha.moduleScope.contracted === 1
        ? 'Medido na 1 empresa que contratou Segurança do Trabalho.'
        : `Medido nas ${trilha.moduleScope.contracted} empresas que contrataram Segurança do Trabalho.`
    );
  }
  return frases;
}

export interface TrackCardProps {
  trilha: OnboardingTrack;
  perspectiva: Perspectiva;
  manifesto: ManifestoDeAcesso | null;
}

export const TrackCard: React.FC<TrackCardProps> = ({ trilha, perspectiva, manifesto }) => {
  const theme = useTheme();
  const texto = TRILHAS[trilha.key];
  if (!texto) return null;

  const { feitos, total } = contagem(trilha);
  const acento = texto.acento === 'grey' ? theme.palette.grey[500] : theme.palette[texto.acento].main;
  const descricao = descricaoDaTrilha(trilha, texto, perspectiva);

  const quemSegura = trilha.blockedBy.map((chave) => ({
    chave,
    titulo: chave in TRILHAS ? escolher(TRILHAS[chave as TrackKey].titulo, perspectiva) : chave,
  }));

  return (
    <Box id={`trilha-${trilha.key}`} component="article" sx={{ scrollMarginTop: 16 }}>
      <SectionCard
        title={escolher(texto.titulo, perspectiva)}
        icon={ICONE_DA_TRILHA[trilha.key]}
        accentColor={acento}
        badge={
          total > 0 ? (
            <Chip
              size="small"
              label={`${feitos} de ${total}`}
              aria-label={`${feitos} de ${total} itens concluídos`}
            />
          ) : undefined
        }
      >
        {(trilha.optional || trilha.blocked) && (
          <Box sx={{ mb: 1 }}>
            <Chip
              size="small"
              variant="outlined"
              icon={trilha.blocked ? <LockIcon /> : undefined}
              label={trilha.blocked ? FRASES.aguardando : FRASES.opcional}
            />
          </Box>
        )}

        {descricao.map((frase) => (
          <Typography key={frase} sx={{ fontSize: '16px', color: 'text.secondary' }}>
            {frase}
          </Typography>
        ))}

        {trilha.blocked && quemSegura.length > 0 && (
          <Box
            role="note"
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              mt: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <InfoIcon fontSize="small" sx={{ mt: 0.25 }} />
            <Typography sx={{ fontSize: '16px' }}>
              Esta parte só fica disponível depois de fechar{' '}
              {quemSegura.map(({ chave, titulo }, i) => (
                <React.Fragment key={chave}>
                  {i > 0 && ', '}
                  <Link
                    href={`#trilha-${chave}`}
                    underline="always"
                    sx={{ display: 'inline-flex', alignItems: 'center', minHeight: 44 }}
                  >
                    {titulo}
                  </Link>
                </React.Fragment>
              ))}
              .
            </Typography>
          </Box>
        )}

        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 2 }}>
          {trilha.items.map((item) => (
            <ItemRow key={item.key} item={item} perspectiva={perspectiva} manifesto={manifesto} />
          ))}
        </Box>
      </SectionCard>
    </Box>
  );
};

export default TrackCard;
